// Unified per-Pokemon metadata API.
// Merges build-time generated data (src/data/generated/*.json, produced by
// scripts/fetchGameData.mjs) with admin overrides (src/data/adminOverrides.js).
// Admin overrides always win.

import GENERATED_ABILITIES from './generated/legalAbilities.json';
import GENERATED_LEARNSETS from './generated/learnsets.json';
import GENERATED_MOVE_USAGE from './generated/moveUsage.json';
import GENERATED_ABILITY_USAGE from './generated/abilityUsage.json';
import { OVERRIDES } from './adminOverrides';

const EMPTY_ABILITIES = { normal: [], hidden: [] };
const EMPTY_USAGE = { singles: {}, doubles: {} };

/**
 * Returns { normal, hidden } arrays of ability names legal for this species.
 * Overrides replace the generated data when present for the species.
 */
export function getLegalAbilities(pokemonName) {
  const override = OVERRIDES.abilities?.[pokemonName];
  if (override) {
    return {
      normal: override.normal || [],
      hidden: override.hidden || [],
    };
  }
  const gen = GENERATED_ABILITIES[pokemonName];
  if (!gen) return EMPTY_ABILITIES;
  return { normal: gen.normal || [], hidden: gen.hidden || [] };
}

/**
 * Returns a flat array of all legal ability names (normal + hidden) for this species.
 */
export function getAllLegalAbilities(pokemonName) {
  const { normal, hidden } = getLegalAbilities(pokemonName);
  // Dedupe while preserving order (normal first).
  const seen = new Set();
  const out = [];
  for (const a of [...normal, ...hidden]) {
    if (!seen.has(a)) { seen.add(a); out.push(a); }
  }
  return out;
}

/**
 * Returns true if this species can legally have the given ability.
 * If we have no data for this species (empty lists), returns true (fail-open)
 * so users aren't locked out of Pokemon we haven't indexed yet.
 */
export function isLegalAbility(pokemonName, abilityName) {
  const legal = getAllLegalAbilities(pokemonName);
  if (legal.length === 0) return true;
  return legal.includes(abilityName);
}

/**
 * Returns true if this ability is the hidden ability slot for this species.
 */
export function isHiddenAbility(pokemonName, abilityName) {
  const { hidden } = getLegalAbilities(pokemonName);
  return hidden.includes(abilityName);
}

/**
 * Returns an array of all legal move names for this species.
 * Applies in order: generated learnset → OVERRIDES.learnsets (replace if present)
 *                 → learnsetAdditions (union) → learnsetRemovals (subtract).
 */
export function getLegalMoves(pokemonName) {
  let base;
  const fullOverride = OVERRIDES.learnsets?.[pokemonName];
  if (fullOverride) {
    base = [...fullOverride];
  } else {
    base = [...(GENERATED_LEARNSETS[pokemonName] || [])];
  }
  const additions = OVERRIDES.learnsetAdditions?.[pokemonName] || [];
  const removals = new Set(OVERRIDES.learnsetRemovals?.[pokemonName] || []);

  const merged = [...base, ...additions].filter(m => !removals.has(m));
  // Dedupe preserving order.
  const seen = new Set();
  const out = [];
  for (const m of merged) {
    if (!seen.has(m)) { seen.add(m); out.push(m); }
  }
  return out;
}

/**
 * Returns true if a Pokemon can legally learn a move.
 * Fail-open if we have no learnset data for this species.
 */
export function isLegalMove(pokemonName, moveName) {
  const legal = getLegalMoves(pokemonName);
  if (legal.length === 0) return true;
  return legal.includes(moveName);
}

/**
 * Returns { singles, doubles } usage percentages (0-100) for a specific move on a Pokemon.
 * Returns 0 for unknown values.
 */
export function getMoveUsage(pokemonName, moveName) {
  const gen = GENERATED_MOVE_USAGE[pokemonName] || EMPTY_USAGE;
  const ovr = OVERRIDES.moveUsage?.[pokemonName] || {};
  const singles = (ovr.singles && ovr.singles[moveName] != null)
    ? ovr.singles[moveName]
    : (gen.singles?.[moveName] ?? 0);
  const doubles = (ovr.doubles && ovr.doubles[moveName] != null)
    ? ovr.doubles[moveName]
    : (gen.doubles?.[moveName] ?? 0);
  return { singles, doubles };
}

/**
 * Returns per-format ability usage percentages for this species.
 * Shape: { singles: { "Intimidate": 87.2, ... }, doubles: { ... } }
 * Overrides can be supplied via OVERRIDES.abilityUsage, merged per-ability.
 */
export function getAbilityUsage(pokemonName) {
  const gen = GENERATED_ABILITY_USAGE[pokemonName] || EMPTY_USAGE;
  const ovr = OVERRIDES.abilityUsage?.[pokemonName] || {};
  return {
    singles: { ...(gen.singles || {}), ...(ovr.singles || {}) },
    doubles: { ...(gen.doubles || {}), ...(ovr.doubles || {}) },
  };
}

/**
 * Returns the most likely ability for a species in the given format.
 * Falls back through: usage data → first normal ability → first hidden ability → null.
 * Always returns an ability the species can legally have, when any legal ability is known.
 */
export function getDefaultAbility(pokemonName, format = 'doubles') {
  const legal = new Set(getAllLegalAbilities(pokemonName));
  const usage = getAbilityUsage(pokemonName);
  const formatUsage = usage[format] || {};
  // Pick the highest-usage ability that is also legal (if legal list known).
  const ranked = Object.entries(formatUsage)
    .filter(([a]) => legal.size === 0 || legal.has(a))
    .sort((a, b) => b[1] - a[1]);
  if (ranked.length > 0) return ranked[0][0];
  // Fall back: first normal ability, then first hidden.
  const { normal, hidden } = getLegalAbilities(pokemonName);
  if (normal.length > 0) return normal[0];
  if (hidden.length > 0) return hidden[0];
  return null;
}

/**
 * Returns true if we have ANY metadata for this species (abilities or learnset).
 * Used by the UI to decide whether to show filtered lists or fall back to the
 * global pool.
 */
export function hasMetadata(pokemonName) {
  return (
    (GENERATED_ABILITIES[pokemonName] != null) ||
    (OVERRIDES.abilities?.[pokemonName] != null) ||
    ((GENERATED_LEARNSETS[pokemonName] || []).length > 0) ||
    (OVERRIDES.learnsets?.[pokemonName] != null)
  );
}
